import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { cn } from 'lib/utils'
import { GripVertical } from 'lucide-react'
import type { FC, PropsWithChildren } from 'react'

interface SortableLinkCardProps extends PropsWithChildren {
  id: string | number
  className?: string
  showBranchLine?: boolean
}

const SortableLinks: FC<SortableLinkCardProps> = ({ id, children, className, showBranchLine }) => {
  const uniqueId = id
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: uniqueId })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const isCursorGrabbing = attributes['aria-pressed']

  return (
    <div ref={setNodeRef} style={style} key={uniqueId}>
      <div
        className={cn(
          'group relative flex justify-between gap-1 border-b border-border bg-background transition-colors hover:bg-bg2 focus-within:bg-bg2',
          className,
        )}
      >
        {showBranchLine ? (
          <div className="branch-structure flex items-center">
            {/* Vertical line */}
            <div className="absolute left-6 top-0 h-full w-[2px] bg-st2" />

            {/* Horizontal branch */}
            <div className="relative flex items-center gap-2">
              <div className="h-[2px] w-6 bg-st2" />

              {/* Dots/handle */}
              <div className="flex items-center justify-center text-tertiary">
                <button
                  type="button"
                  {...attributes}
                  {...listeners}
                  className={cn(
                    'flex h-full w-7 items-center justify-center rounded-sm transition-colors hover:bg-hv1 hover:text-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                    isCursorGrabbing ? 'cursor-grabbing' : 'cursor-grab',
                  )}
                  aria-describedby={`DndContext-${uniqueId}`}
                  aria-label="Reorder item"
                >
                  <GripVertical className="size-3.5" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center text-tertiary">
            <button
              type="button"
              {...attributes}
              {...listeners}
              className={cn(
                'flex h-full w-7 items-center justify-center rounded-sm transition-colors hover:bg-hv1 hover:text-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                isCursorGrabbing ? 'cursor-grabbing' : 'cursor-grab',
              )}
              aria-describedby={`DndContext-${uniqueId}`}
              aria-label="Reorder item"
            >
              <GripVertical className="size-3.5" strokeWidth={1.5} />
            </button>
          </div>
        )}

        <div className="w-full">{children}</div>
      </div>
    </div>
  )
}

export default SortableLinks
